import React, { useMemo } from 'react';
import FilterChip from './FilterChip';
import { quickFilters } from '../utils/helper';
import { useStoreSelector } from '@ui/hooks/useStoreSelector';
import useOpportunitiesStore from '../store/useOpportunitiesStore';
import { useSearchParams } from 'next/navigation';
import useWindowDimensions from '@hooks/useWindowDimensions';
import {
    oppListingFilterKeys,
    SORT_OPTIONS,
} from '@components/OpportunityListing/utils/constants';
import SortMenu from './FilterAndSort/SortMenu';
import { isArray } from 'lodash';
import { getAppliedFilters } from '@components/OpportunityListing/utils/utils';
import Button from '@ui/atoms/design-system/Button';
import { useRouter } from 'next/router';
import { buttonSizes, buttonTypes } from '@ui/atoms/utils/enums';
import { Text } from '@ui/atoms';

const OpportunityFilters = ({
    updateQueryParams,
    opportunitiesCount,
    clearAllFilters,
}) => {
    const { openSortModal, showSortModal, openFilterModal, closeSortModal } =
        useStoreSelector(useOpportunitiesStore, (state) => ({
            openSortModal: state.openSortModal,
            showSortModal: state.showSortModal,
            openFilterModal: state.openFilterModal,
            closeSortModal: state.closeSortModal,
        }));
    const { width } = useWindowDimensions();
    const searchParams = useSearchParams();
    const router = useRouter();
    const { query } = router;

    const queryParams = Object.fromEntries(searchParams.entries());
    const {
        count: filterCount,
        isSorted,
        sortFiled,
        chips: advancedFilterChips,
    } = getAppliedFilters(queryParams);

    const handleFilters = async (
        valuesArray,
        isSelected,
        filter,
        isQuickFilter = true
    ) => {
        const newValuesArray = isSelected
            ? valuesArray.filter((v) => v !== filter.value)
            : [...valuesArray, filter.value];

        const newValue =
            newValuesArray.length > 0 ? newValuesArray.join(',') : null;

        const updatedParams = { [filter.key]: newValue };

        const existingFiltersCount = oppListingFilterKeys.filter(
            (param) => param in query
        ).length;

        if (existingFiltersCount <= 1 && !newValue) {
            updatedParams.quickFilters = false;
        } else if (!query.quickFilters && isQuickFilter) {
            updatedParams.quickFilters = true;
        }
        updatedParams[filter.key] = newValue;

        await updateQueryParams(updatedParams);
    };

    const getSortByLabel = useMemo(() => {
        if (sortFiled?.sortOrder && sortFiled?.sortBy) {
            const sortLabel = SORT_OPTIONS.find(
                (option) =>
                    option.value ===
                    `${sortFiled.sortOrder}_${sortFiled.sortBy}`
            );
            if (sortLabel) {
                // Prevents unnecessary state updates
                return sortLabel.label;
            }
        }
        return 'Sort';
    }, [sortFiled]);

    const renderClearAllButton = (className) => {
        return filterCount > 0 ? (
            <Button
                buttonText="Clear Filters"
                buttonSize={buttonSizes.EXTRA_SMALL}
                buttonType={buttonTypes.TEXT_UNDERLINE}
                onClick={() => {
                    const { type, sort, sortBy } = queryParams;
                    clearAllFilters({ type, sort, sortBy });
                }}
                className={className}
            />
        ) : null;
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-row gap-3 md:items-start md:flex-row-reverse md:justify-between overflow-auto md:overflow-visible scrollbar-hide">
                {/* Filters */}

                <div
                    className="relative flex flex-row-reverse md:flex-row items-center gap-3 "
                    onClick={(e) => e.stopPropagation()}
                >
                    {query.quickFilters ? null : (
                        <FilterChip
                            label={'Filter'}
                            canClear={false}
                            onClick={openFilterModal}
                            isSelected={filterCount > 0}
                            icon={`/icons/${
                                filterCount > 0
                                    ? 'SliderHorizontalBlue'
                                    : 'SliderHorizontal'
                            }.svg`}
                            count={filterCount}
                            isFilterOrSortChip
                        />
                    )}
                    <FilterChip
                        label={getSortByLabel}
                        canClear={false}
                        onClick={showSortModal ? closeSortModal : openSortModal}
                        isSelected={isSorted}
                        icon={`/icons/${
                            isSorted ? 'ArrowsDownUpBlue' : 'ArrowsDownUp'
                        }.svg`}
                        isFilterOrSortChip
                    />
                    {showSortModal && width >= 768 ? (
                        <SortMenu
                            onClose={closeSortModal}
                            sortOptions={SORT_OPTIONS}
                            defaultSort={SORT_OPTIONS[0].value}
                        />
                    ) : null}
                </div>
                <div className="flex items-start flex-1 ">
                    {filterCount && !query.quickFilters ? (
                        <div className="hidden md:flex flex-wrap items-center gap-3">
                            {advancedFilterChips.length > 0 ? (
                                <FilterChipsList
                                    filters={advancedFilterChips}
                                    searchParams={searchParams}
                                    handleFilters={handleFilters}
                                    isQuickFilter={false}
                                />
                            ) : null}
                            {renderClearAllButton('hidden md:block p5-medium')}
                        </div>
                    ) : (
                        <div className="flex items-center flex-1 gap-3">
                            <Text
                                content={'Quick Filters'}
                                className={'p5-semibold text-gray-900 hidden lg:block'}
                            />
                            <FilterChipsList
                                filters={quickFilters}
                                searchParams={searchParams}
                                handleFilters={handleFilters}
                                isMobile={
                                    typeof window !== 'undefined' && width < 768
                                }
                                showOnlySelectedOnMobile
                            />
                            {renderClearAllButton('hidden md:block')}
                        </div>
                    )}
                </div>
            </div>
            {filterCount > 0 ? (
                <div
                    className={`flex items-center gap-3 ${
                        opportunitiesCount ? 'justify-between' : 'justify-end'
                    }`}
                >
                    {renderClearAllButton('md:hidden')}
                    {query.quickFilters ? (
                        <div className="flex items-center flex-1 gap-3 overflow-auto scrollbar-hide md:hidden ">
                            <FilterChipsList
                                filters={quickFilters}
                                searchParams={searchParams}
                                handleFilters={handleFilters}
                            />
                        </div>
                    ) : (
                        <div className="flex items-center flex-1 overflow-x-auto gap-4 md:hidden scrollbar-hide">
                            {advancedFilterChips.length > 0 ? (
                                <FilterChipsList
                                    filters={advancedFilterChips}
                                    searchParams={searchParams}
                                    handleFilters={handleFilters}
                                    isQuickFilter={false}
                                />
                            ) : null}
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
};

const FilterChipsList = ({
    filters,
    searchParams,
    handleFilters,
    isMobile = false,
    showOnlySelectedOnMobile = false,
    isQuickFilter = true,
}) => {
    return filters.map((filter) => {
        const { label, key, id, value } = filter;
        const currentValue = searchParams.get(key) || '';
        const valuesArray = isArray(currentValue)
            ? currentValue
            : currentValue.split(',').filter(Boolean);
        const isSelected = isQuickFilter ? valuesArray.includes(value) : true;
        if (isMobile && isSelected && showOnlySelectedOnMobile && isQuickFilter)
            return null; // To not show selected filters on first line on mobile for quick filters
        if (!showOnlySelectedOnMobile && !isSelected && isQuickFilter)
            return null; // To not show unselected filters on mobile for quick filters in second line
        return (
            <FilterChip
                key={id}
                label={label}
                canClear
                className="min-w-max"
                onClick={() => {
                    handleFilters(
                        valuesArray,
                        isSelected,
                        filter,
                        isQuickFilter
                    );
                }}
                isSelected={isSelected}
            />
        );
    });
};

export default OpportunityFilters;
